/**
 * Optimize a user-uploaded image before sending it to the AI generator.
 *
 * Phone cameras commonly produce 5–15 MB photos at 4000+px. This utility:
 *   1. Downscales to maxDimension on the longer side
 *   2. Encodes as JPEG at `quality`
 *   3. If `targetBytes` is set and the result is still too large, binary-searches
 *      quality between 0.3 and the initial quality (7 iterations) until the
 *      output fits. If quality reduction alone is not enough, it also reduces
 *      dimensions proportionally.
 *
 * Returns the original file untouched when:
 *   - The file is already small (under maxBytes AND targetBytes) and fits within maxDimension
 *   - The browser cannot decode it (rare; we fall back gracefully)
 *   - The re-encoded output ended up *larger* than the original
 *
 * @param {File}   file
 * @param {object} [opts]
 * @param {number} [opts.maxDimension=1600]  Cap on longer side in px
 * @param {number} [opts.quality=0.85]       Initial JPEG quality (0–1)
 * @param {number} [opts.maxBytes=500_000]   Skip optimisation if already under this AND fits in maxDimension
 * @param {number} [opts.targetBytes=null]   Hard output-size cap; null = no enforcement
 * @returns {Promise<File>}
 */
export async function optimizeImage(file, opts = {}) {
    const {
        maxDimension = 1600,
        quality      = 0.85,
        maxBytes     = 500_000,
        targetBytes  = null,
    } = opts;

    if (!file || !file.type?.startsWith('image/')) return file;

    // Skip GIFs / SVGs — re-encoding through canvas loses animation / vectors.
    if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;

    // Cheap early-out: already small AND within dimension limits. A file under
    // targetBytes needs no recompression — re-encoding it costs ~1s of blocked
    // main thread and usually produces a LARGER file that gets discarded anyway.
    const sizeCap = targetBytes ? Math.min(maxBytes, targetBytes) : maxBytes;
    if (file.size <= sizeCap) {
        const dims = await readImageDimensions(file).catch(() => null);
        if (dims && Math.max(dims.width, dims.height) <= maxDimension) return file;
    }

    let bitmap;
    try {
        bitmap = await loadImageBitmap(file);
    } catch {
        return file; // browser can't decode — pass through
    }

    const { width: srcW, height: srcH } = bitmap;
    const longer = Math.max(srcW, srcH);
    const scale  = longer > maxDimension ? maxDimension / longer : 1;
    let w = Math.round(srcW * scale);
    let h = Math.round(srcH * scale);

    const canvas = document.createElement('canvas');
    canvas.width  = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    let blob = await blobAt(canvas, quality);
    if (!blob) return file;

    // ── Enforce targetBytes if requested ─────────────────────────────────────
    if (targetBytes && blob.size > targetBytes) {
        // Phase 1: binary-search quality (7 iterations ≈ ±0.004 precision)
        let lo = 0.3, hi = quality, bestBlob = null;
        for (let i = 0; i < 7; i++) {
            const mid = (lo + hi) / 2;
            const candidate = await blobAt(canvas, mid);
            if (candidate && candidate.size <= targetBytes) {
                bestBlob = candidate;
                lo = mid;   // can afford higher quality
            } else {
                hi = mid;   // still too big, go lower
            }
        }
        if (bestBlob) {
            blob = bestBlob;
        } else {
            // Phase 2: quality alone wasn't enough — shrink dimensions too.
            // Estimate a scale factor from the size ratio (with a 10 % safety margin).
            const ratio = Math.sqrt(targetBytes / blob.size) * 0.9;
            const w2 = Math.max(Math.round(w * ratio), 100);
            const h2 = Math.max(Math.round(h * ratio), 100);
            const c2 = document.createElement('canvas');
            c2.width  = w2;
            c2.height = h2;
            const ctx2 = c2.getContext('2d');
            ctx2.drawImage(canvas, 0, 0, w2, h2);
            // Use a moderate quality on the smaller canvas
            blob = (await blobAt(c2, 0.75)) || blob;
        }
    }

    // Never return a larger file than what the user uploaded
    if (blob.size >= file.size) return file;

    const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
    });
}

// ── helpers ──────────────────────────────────────────────────────────────────

function blobAt(canvas, q) {
    return new Promise(resolve =>
        canvas.toBlob(b => resolve(b || null), 'image/jpeg', q)
    );
}

function loadImageBitmap(file) {
    if (typeof createImageBitmap === 'function') return createImageBitmap(file);
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
        img.src = url;
    });
}

function readImageDimensions(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload  = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
        img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
        img.src = url;
    });
}
